import { Observable, ReplaySubject, of, zip } from "rxjs";
import { concatMap, map, switchMap, tap } from "rxjs/operators";

import { AssociatedMedias, AssociatedMediasResource, EntityType, PushCachedValueResult, ReplaySubjectContent, SynchroMediaUpdates } from "@skinsoft/types";
import { SynchroUpHandler, SyncUpDependency } from "@skinsoft/classes-interfaces-definition";

/**
 * @type T type of entity stored inside CACHE-UP
 * @type U type of entity pushed to backend
 * @type A type of after push additionnalData defaults to any
 */
export abstract class AbstractSyncUpHandler<T, U, A = any> implements SynchroUpHandler {
    abstract getCachedObjectsIds(): Observable<string[]>
    abstract getDependenciesIds(id: string): Observable<SyncUpDependency[]>
    
    abstract getCachedEntity(key: string): Observable<T>

    protected cancelAsked: boolean = false
    protected locale: string | undefined = undefined
    
    abstract getNbCachedObjects(countLocallyCreated?: boolean | undefined): Promise<number> 
    abstract getNbLocallyCreatedEntities(): Promise<number>
    abstract getType(): EntityType

    syncUp(id: string): Observable<[boolean, string]> {
        console.info(`Starting SYNC-UP of ${id}`)

        return this.getCachedEntity(id).pipe(
            switchMap(cachedEntity => {
                return this.formatDataBeforePush(cachedEntity)
            }),
            switchMap(formattedEntity => {
                return of({
                    id: id, 
                    body: formattedEntity, 
                } as SynchroMediaUpdates<U>)
            }),
            switchMap(dataToPush => {
                return this.applyBeforePushOperations(dataToPush)
            }),
            switchMap(([beforePushRes, dataToUpdate]) => {
                if(beforePushRes.success && dataToUpdate) {
                    let observable$: Observable<PushCachedValueResult>
                    
                    if(this.isCachedCreation(dataToUpdate.id)) {
                        observable$ = this.pushCachedCreations(dataToUpdate).pipe(
                            switchMap(res => {
                                if(res.success) {
                                    if(res.newId) {
                                        this.saveMappingOldIdNewId(dataToUpdate.id, res.newId)
                                    }
                                }
                                return of(res)
                            })
                        )
                    } else {
                        observable$ = this.pushCachedUpdate(dataToUpdate)
                    }

                    return observable$.pipe(
                        switchMap(res => {
                            if(res.success) {
                                return zip(
                                    of(res),
                                    this.getAfterPushAdditionalData(dataToUpdate)
                                )
                            }

                            return zip(
                                of(res),
                                of(undefined)
                            )
                        }),
                        switchMap(([res, additionalData]) => {
                            return this.applyAfterPushOperations(res, dataToUpdate, additionalData)
                        }),
                        tap(res => {
                            if(res.success) {
                                const cacheKeysToClean = this.getCacheKeysToClean(res, dataToUpdate)
                                this.cleanCacheAfterPush(cacheKeysToClean)
                            }
                        }),
                        map(res => {
                            return [res.success, id] as [boolean, string]
                        })
                    )
                }

                return of([false, id] as [boolean, string])
            })
        )
    }

    async handleSynchroUpdateFromCache(pushNewEntities: boolean = true, locale?: string): Promise<Observable<boolean>> {
        this.cancelAsked = false
        if(locale) {
            this.locale = locale
        }

        return this.getAllCachedEntities(pushNewEntities).pipe(
            switchMap(cachedEntities => {
                return of(...cachedEntities)
            }),
            concatMap(cachedEntity => {
                return this.formatDataBeforePush(cachedEntity)
            }),
            concatMap(formattedEntity => {
                try {
                    if(!this.cancelAsked) {
                        const localAssociatedMedias = this.getLocalAssociatedMedias(formattedEntity)
                        const localMediasKeys = this.getLocalMediasKeys(formattedEntity)
                        const mediaReplaySubjects = this.buildUploadCachedMediasReplaySubject(localMediasKeys)
        
                        const formattedEntityWithoutLocalMedias = this.removeLocalMedias(formattedEntity)
        
                        return zip(
                            of({
                                success: true
                            } as PushCachedValueResult),
                            of({ 
                                id: this.getEntityId(formattedEntity), 
                                body: formattedEntityWithoutLocalMedias, 
                                mediaSubjects: mediaReplaySubjects, 
                                cachedMedias: localAssociatedMedias as AssociatedMedias, 
                            }),
                        )
                    }
                } catch(err) {}

                return zip(
                    of({
                        success: false
                    } as PushCachedValueResult),
                    of(null),
                )
            }),
            concatMap(([result, dataToPush]) => {
                if(result.success && dataToPush) {
                    return this.applyBeforePushOperations(dataToPush)
                }

                return zip(
                    of({
                        success: false
                    } as PushCachedValueResult),
                    of(null)
                )
            }),
            concatMap(([beforePushRes, dataToUpdate]) => {
                if(beforePushRes.success && dataToUpdate) {
                    let observable$: Observable<PushCachedValueResult>
                    
                    if(this.isCachedCreation(dataToUpdate.id)) {
                        observable$ = this.pushCachedCreations(dataToUpdate).pipe(
                            switchMap(res => {
                                if(res.success) {
                                    if(res.newId) {
                                        this.saveMappingOldIdNewId(dataToUpdate.id, res.newId)
                                    }
                                }
                                return of(res)
                            })
                        )
                    } else {
                        observable$ = this.pushCachedUpdate(dataToUpdate)
                    }

                    return observable$.pipe(
                        switchMap(res => {
                            if(res.success) {
                                return zip(
                                    of(res),
                                    this.getAfterPushAdditionalData(dataToUpdate)
                                )
                            }

                            return zip(
                                of(res),
                                of(undefined)
                            )
                        }),
                        switchMap(([res, additionalData]) => {
                            return this.applyAfterPushOperations(res, dataToUpdate, additionalData)
                        }),
                        tap(res => {
                            if(res.success) {
                                const cacheKeysToClean = this.getCacheKeysToClean(res, dataToUpdate)
                                this.cleanCacheAfterPush(cacheKeysToClean)
                            }
                        }),
                        map(res => {
                            return res.success
                        })
                    )
                }

                return of(false)
            }),
        )
    }

    abstract getAllCachedEntities(includeCashedCreations: boolean): Observable<T[]>
    abstract formatDataBeforePush(entity: T): Observable<U>
    abstract removeLocalMedias(entity: U): U
    abstract getEntityId(entity: U): string 
    abstract getLocalAssociatedMedias(entity: U): AssociatedMediasResource
    abstract getLocalMediasKeys(entity: U): string[] | null
    abstract buildUploadCachedMediasReplaySubject(localMediasKeys: string[] | null): { [replayName: string]: ReplaySubject<ReplaySubjectContent> }
    abstract applyBeforePushOperations(dataToPush: SynchroMediaUpdates<U>): Observable<[PushCachedValueResult, SynchroMediaUpdates<U>]>
    
    abstract isCachedCreation(id: string): boolean
    abstract pushCachedUpdate(dataToUpdate: SynchroMediaUpdates<U>): Observable<PushCachedValueResult>
    abstract pushCachedCreations(dataToCreate: SynchroMediaUpdates<U>): Observable<PushCachedValueResult>

    abstract saveMappingOldIdNewId(oldId: string, newId: string): void
    abstract getAfterPushAdditionalData(dataToPush: SynchroMediaUpdates<U>): Observable<A>
    abstract applyAfterPushOperations(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<U>, additionalData?: A): Observable<PushCachedValueResult> 
    
    protected getCacheKeysToClean(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<U>): string[] {
        const cacheKeysToClean = [dataPushed.id]
        if(pushResult.newId) {
            cacheKeysToClean.push(pushResult.newId)
        }
        return cacheKeysToClean        
    }
    abstract cleanCacheAfterPush(cacheKeys: string[]): void
    
    public abortPush() {
        this.cancelAsked = true
    }
}